// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { contactAPI } from "@/lib/api";
import { workspaceAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  GraduationCap,
  LayoutDashboard,
  BookOpen,
  FileText,
  Video,
  MessageSquare,
  FolderOpen,
  Users,
  Settings,
  LogOut,
  User,
  Menu,
  Shield,
  ChevronLeft,
  Home,
  FlaskConical,
  ClipboardList,
  PenTool,
  Globe,
  Code,
  Clapperboard,
  CalendarDays,
  UserCheck,
  FileX,
  TestTube2,
  ChevronDown,
  ChevronRight,
  Layers,
  Monitor,
  Mic,
  StickyNote,
  Archive,
  Lock,
} from "lucide-react";
import { getInitials } from "@/lib/utils";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import FloatingNotepad from "@/components/widgets/FloatingNotepad";
import ChatDialog from "@/components/chat/ChatDialog";
import ChatBubbleNotification from "@/components/chat/ChatBubbleNotification";
import { useChatSocket } from "@/hooks/useChatSocket";


export const WorkspaceLayout = ({ children }) => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isManager, canManage, permissions } = useAuth();
  const { emitTyping } = useChatSocket(user);
  const [workspace, setWorkspace] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [badgeCounts, setBadgeCounts] = useState({});

  // Fetch badge counts periodically
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await contactAPI.getBadgeCounts(workspaceId);
        setBadgeCounts(res.data);
      } catch(e) {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (workspaceId) {
      loadWorkspace();
    }
  }, [workspaceId]);

  const loadWorkspace = async () => {
    try {
      const response = await workspaceAPI.get(workspaceId);
      setWorkspace(response.data);
    } catch (error) {
      console.error("Failed to load workspace:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Permission check helper - admins always have access
  const hasPermission = (field) => {
    if (isAdmin) return true;
    if (!permissions) return true; // Default to true if permissions not loaded
    return permissions[field] !== false;
  };

  const buildNavItems = () => {
    const items = [
      { href: `/workspace/${workspaceId}`, label: "Vue d'ensemble", icon: LayoutDashboard, exact: true },
    ];

    if (hasPermission('can_access_courses')) {
      items.push({ href: `/workspace/${workspaceId}/courses`, label: "Cours", icon: BookOpen });
    }

    // Grouping "Ressources Cours"
    const resGroup = {
      label: "Ressources Cours",
      icon: Layers,
      isGroup: true,
      subItems: []
    };

    if (hasPermission('can_access_tp')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/tp`, label: "TP", icon: FlaskConical });
    if (hasPermission('can_access_assignments')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/assignments`, label: "Devoirs", icon: FileText });
    resGroup.subItems.push({ href: `/workspace/${workspaceId}/url-resources`, label: "Liens URL", icon: Globe });
    if (hasPermission('can_access_quiz')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/quiz`, label: "Quiz", icon: ClipboardList });
    if (hasPermission('can_access_codeshare')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/codeshare`, label: "CodeShare", icon: Code });
    if (hasPermission('can_access_draw')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/draw`, label: "Draw", icon: PenTool });
    if (hasPermission('can_access_meetings')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/meetings`, label: "Réunions", icon: Video });
    if (hasPermission('can_access_videos')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/videos`, label: "Capture Vidéo", icon: Clapperboard });
    if (hasPermission('can_access_exams')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/exams`, label: "Examens", icon: ClipboardList });
    if (hasPermission('can_access_labs')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/labs`, label: "Labs", icon: TestTube2 });
    if (hasPermission('can_access_vms')) resGroup.subItems.push({ href: `/workspace/${workspaceId}/vms`, label: "VMs", icon: Monitor });
    resGroup.subItems.push({ href: `/workspace/${workspaceId}/notes`, label: "Notes", icon: StickyNote });

    if (resGroup.subItems.length > 0) {
      items.push(resGroup);
    }

    if (isAdmin && hasPermission('can_access_sites')) {
      items.push({ href: `/workspace/${workspaceId}/sites`, label: "Site Deployer", icon: Globe });
    }
    
    if (hasPermission('can_access_chat')) items.push({ href: `/workspace/${workspaceId}/chat`, label: "Chat", icon: MessageSquare, badge: badgeCounts.chat || null, badgeColor: "bg-violet-500" });
    if (hasPermission('can_access_files')) items.push({ href: `/workspace/${workspaceId}/files`, label: "Fichiers", icon: FolderOpen });
    if (hasPermission('can_access_planning')) items.push({ href: `/workspace/${workspaceId}/planning`, label: "Planning", icon: CalendarDays });
    if (hasPermission('can_access_attendance')) items.push({ href: `/workspace/${workspaceId}/attendance`, label: "Présence", icon: UserCheck });
    if (hasPermission('can_access_absence_requests')) items.push({ href: `/workspace/${workspaceId}/absence-requests`, label: "Demandes d'absence", icon: FileX });
    if (hasPermission('can_access_progress')) items.push({ href: `/workspace/${workspaceId}/progress`, label: "Progression", icon: LayoutDashboard });
    if (canManage) items.push({ href: `/workspace/${workspaceId}/evaluations`, label: "Évaluations", icon: ClipboardList });
    if (canManage) items.push({ href: `/workspace/${workspaceId}/interviews`, label: "Entretiens", icon: Mic });
    if (canManage) items.push({ href: `/workspace/${workspaceId}/reports`, label: "Rapports", icon: FileText });

    if (isManager) {
      if (hasPermission('can_access_participants')) {
        items.push({ href: `/workspace/${workspaceId}/participants`, label: "Participants", icon: Users });
      }
    } else if (canManage && hasPermission('can_access_participants')) {
      items.push({ href: `/workspace/${workspaceId}/participants`, label: "Participants", icon: Users });
    }

    return items;
  };

  const workspaceNavItems = buildNavItems();

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    // Match exactly or when followed by a slash (to avoid /tp matching /training etc.)
    return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
  };

  // Check if any sub-item of a group is currently active
  const isGroupActive = (group) => {
    return group.subItems?.some(sub => isActive(sub));
  };

  const NavContent = () => {
    const toggleGroup = (groupLabel) => {
      setOpenGroups(prev => ({ ...prev, [groupLabel]: !prev[groupLabel] }));
    };

    return (
      <div className="flex h-full flex-col">
        {/* Logo & Back */}
        <div className="flex h-16 items-center gap-2 border-b border-border/50 px-4">
          <Link to="/dashboard" className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all duration-200">
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 shadow-sm">
            <GraduationCap className="h-4 w-4 text-white" />
          </div>
          <span className="text-base font-bold truncate flex-1">{workspace?.name || "Chargement..."}</span>
        </div>

        {/* Workspace Navigation */}
        <nav className="flex-1 space-y-0.5 p-3 stagger-children overflow-y-auto scrollbar-thin">
          {workspaceNavItems.map((item) => {
            if (item.isGroup) {
              const groupActive = isGroupActive(item);
              const isOpen = openGroups[item.label] !== undefined ? openGroups[item.label] : groupActive;
              return (
                <div key={item.label} className="space-y-1 mt-1 mb-2">
                  <button 
                    onClick={() => toggleGroup(item.label)}
                    className="w-full sidebar-item flex items-center justify-between font-medium"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5 text-muted-foreground" />
                      <span>{item.label}</span>
                    </div>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </button>
                  {isOpen && (
                    <div className="ml-[22px] pl-3 border-l border-border/50 space-y-0.5 mt-1">
                      {item.subItems.map(sub => {
                        const active = isActive(sub);
                        return (
                          <Link key={sub.href} to={sub.href} className={`sidebar-item text-sm py-2 ${active ? "active" : ""}`}>
                            <sub.icon className="h-4 w-4" />
                            <span>{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActive(item);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`sidebar-item ${active ? "active" : ""}`}
                data-testid={`ws-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
                {item.badge && (
                  <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full ${item.badgeColor || 'bg-primary'} text-white`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

      {/* Quick Links */}
      <div className="border-t p-4 space-y-1">
        <Link
          to="/dashboard"
          className="sidebar-item"
          data-testid="ws-nav-dashboard"
        >
          <Home className="h-5 w-5" />
          <span>Retour au tableau de bord</span>
        </Link>
      </div>

      {/* User section */}
      <div className="border-t border-border/50 p-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-9 w-9 ring-2 ring-primary/20">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-sm font-bold">
                {getInitials(user?.first_name, user?.last_name)}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-white" />
          </div>
          <div className="flex-1 truncate">
            <p className="text-sm font-semibold truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="app-shell">
      {/* Desktop Sidebar */}
      <aside className="app-sidebar">
        <NavContent />
      </aside>

      {/* Main Content */}
      <div className="app-main">
        {/* Top Header */}
        <header className="glass sticky top-0 z-40 flex h-16 items-center justify-between px-4 md:px-6">
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <NavContent />
            </SheetContent>
          </Sheet>

          {/* Breadcrumb */}
          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/dashboard" className="hover:text-foreground">Tableau de bord</Link>
            <span>/</span>
            <span className="text-foreground font-medium">{workspace?.name}</span>
          </div>

          <div className="flex-1" />

          {/* Signature */}
          <div className="hidden md:flex items-center mr-2">
            <span className="text-xs font-semibold text-primary/80 tracking-wide border border-primary/30 rounded-full px-3 py-1 bg-primary/5">
              KHLIFI HOUCEM &nbsp;|&nbsp; FORMATEUR CLOUD &amp;&amp; DEVSECOPS
            </span>
          </div>

          {/* Notifications */}
          <NotificationBell />

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline-block">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="mr-2 h-4 w-4" />
                Profil
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")}>
                  <Shield className="mr-2 h-4 w-4" />
                  Administration
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main style={{ position: "relative" }}>
          {workspace?.is_archived && (
            <>
              {/* Archived banner */}
              <div style={{
                position: "sticky", top: 0, zIndex: 50,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "10px 20px",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#fff", fontWeight: 700, fontSize: 13,
                letterSpacing: 0.3,
                boxShadow: "0 2px 8px rgba(245,158,11,0.3)"
              }}>
                <Archive style={{ width: 16, height: 16 }} />
                <span>Cet espace de travail est archivé — Mode lecture seule</span>
                <Lock style={{ width: 14, height: 14, opacity: 0.7 }} />
              </div>
              {/* Overlay to block interactions */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 40, cursor: "not-allowed",
                pointerEvents: "auto",
                background: "transparent"
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              />
            </>
          )}
          <div style={workspace?.is_archived ? { pointerEvents: "none", opacity: 0.7, filter: "grayscale(0.2)" } : {}}>
            {children}
          </div>
        </main>


      </div>
      <FloatingNotepad />
      <ChatDialog emitTyping={emitTyping} />
      <ChatBubbleNotification />
    </div>
  );
};
